import type {
  CommunicationsFocusMode,
  CrmPatientSummary,
  FluxZapCommunicationRecord,
  TemplateSendAggregateRow,
} from "./types";

export function patientIdFromReference(reference: string): string | undefined {
  const prefix = "Patient/";
  if (!reference.startsWith(prefix)) return undefined;
  return reference.slice(prefix.length);
}

/** Sexo administrativo no filtro “Encontrar pacientes”; “all” não restringe. */
export type PatientFinderGender = "all" | "male" | "female" | "other";

/** Comunicação FluxZap; “all” não restringe. */
export type PatientFinderCommunicationFilter = "all" | "never" | "already";

export function isPatientFinderCommunicationFilter(
  value: string,
): value is PatientFinderCommunicationFilter {
  return value === "all" || value === "never" || value === "already";
}

/**
 * Conjunto de patientId que já aparecem em algum envio (mock ou futuro FHIR).
 * Usado para “Ainda não me comuniquei”.
 */
export function buildCommunicatedPatientIdSet(
  communications: FluxZapCommunicationRecord[],
): Set<string> {
  const out = new Set<string>();
  for (const c of communications) {
    const id = patientIdFromReference(c.subjectPatientReference);
    if (id) out.add(id);
  }
  return out;
}

/**
 * Idade em anos completos na data de referência (aniversário já ocorreu ou não).
 */
export function ageYearsFromBirthDate(birthDateIso: string, ref: Date): number {
  const parts = birthDateIso.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]) - 1;
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return NaN;
  }
  let age = ref.getFullYear() - y;
  const refMd = ref.getMonth() * 32 + ref.getDate();
  const birthMd = m * 32 + d;
  if (refMd < birthMd) age -= 1;
  return age;
}

export interface PatientFinderCriteria {
  gender: PatientFinderGender;
  communicationFilter: PatientFinderCommunicationFilter;
  communicatedPatientIds: Set<string>;
  minAgeYears: number | null;
  maxAgeYears: number | null;
  /** ISO YYYY-MM-DD inclusive; null = sem limite inferior/ superior. */
  lastAppointmentFrom: string | null;
  lastAppointmentTo: string | null;
  /** “Hoje” para idade; pode ser fixado ao abrir o diálogo. */
  refDate: Date;
}

/**
 * Combina critérios com AND (todos os filtros preenchidos se aplicam em conjunto).
 * Intervalo de última consulta: pacientes sem `lastAppointmentAt` ficam de fora
 * quando qualquer limite de data está definido.
 */
export function applyPatientFinderFilters(
  patients: CrmPatientSummary[],
  criteria: PatientFinderCriteria,
): CrmPatientSummary[] {
  return patients.filter((p) => {
    if (criteria.gender !== "all") {
      const g = p.gender ?? "unknown";
      if (g !== criteria.gender) return false;
    }
    if (criteria.communicationFilter === "never") {
      if (criteria.communicatedPatientIds.has(p.patientId)) return false;
    }
    if (criteria.communicationFilter === "already") {
      if (!criteria.communicatedPatientIds.has(p.patientId)) return false;
    }
    const age = ageYearsFromBirthDate(p.birthDate, criteria.refDate);
    if (!Number.isFinite(age)) return false;
    if (criteria.minAgeYears != null && age < criteria.minAgeYears) {
      return false;
    }
    if (criteria.maxAgeYears != null && age > criteria.maxAgeYears) {
      return false;
    }
    if (
      criteria.lastAppointmentFrom != null ||
      criteria.lastAppointmentTo != null
    ) {
      const last = p.lastAppointmentAt;
      if (!last) return false;
      if (
        criteria.lastAppointmentFrom != null &&
        last < criteria.lastAppointmentFrom
      ) {
        return false;
      }
      if (
        criteria.lastAppointmentTo != null &&
        last > criteria.lastAppointmentTo
      ) {
        return false;
      }
    }
    return true;
  });
}

export function aggregateTemplateSends(
  communications: FluxZapCommunicationRecord[],
): TemplateSendAggregateRow[] {
  const map = new Map<string, { label: string; count: number }>();
  for (const row of communications) {
    const prev = map.get(row.templateCode);
    if (prev) {
      prev.count += 1;
    } else {
      map.set(row.templateCode, {
        label: row.templateLabel,
        count: 1,
      });
    }
  }
  return [...map.entries()]
    .map(([templateCode, { label, count }]) => ({
      templateCode,
      templateLabel: label,
      sendCount: count,
    }))
    .sort((a, b) => b.sendCount - a.sendCount);
}

export function buildPatientLookup(
  patients: CrmPatientSummary[],
): Map<string, CrmPatientSummary> {
  return new Map(patients.map((p) => [p.patientId, p]));
}

export interface PatientCommunicationRow {
  patient: CrmPatientSummary;
  messages: FluxZapCommunicationRecord[];
}

export function groupCommunicationsByPatient(
  patients: CrmPatientSummary[],
  communications: FluxZapCommunicationRecord[],
): PatientCommunicationRow[] {
  const lookup = buildPatientLookup(patients);
  const byPatient = new Map<string, FluxZapCommunicationRecord[]>();

  for (const msg of communications) {
    const pid = patientIdFromReference(msg.subjectPatientReference);
    if (!pid) continue;
    const list = byPatient.get(pid) ?? [];
    list.push(msg);
    byPatient.set(pid, list);
  }

  return patients.map((patient) => ({
    patient,
    messages: (byPatient.get(patient.patientId) ?? []).sort((a, b) =>
      a.created < b.created ? 1 : -1,
    ),
  }));
}

/**
 * Agrupa comunicações por destinatário real (id do `subject`).
 * Usado no modo vet quando o destinatário é o Patient lógico do tutor (CPF),
 * não necessariamente um pet listado no contexto da organização.
 */
export function groupCommunicationsByRecipient(
  recipientLookup: Map<string, CrmPatientSummary>,
  communications: FluxZapCommunicationRecord[],
  resolveMissingRecipient: (patientId: string) => CrmPatientSummary,
): PatientCommunicationRow[] {
  const byRecipient = new Map<string, FluxZapCommunicationRecord[]>();

  for (const msg of communications) {
    const pid = patientIdFromReference(msg.subjectPatientReference);
    if (!pid) continue;
    const list = byRecipient.get(pid) ?? [];
    list.push(msg);
    byRecipient.set(pid, list);
  }

  return [...byRecipient.entries()]
    .map(([patientId, messages]) => ({
      patient:
        recipientLookup.get(patientId) ?? resolveMissingRecipient(patientId),
      messages: messages.sort((a, b) => (a.created < b.created ? 1 : -1)),
    }))
    .filter((row) => row.messages.length > 0);
}

/** Pacientes sem atendimento registrado no intervalo [from, to] (datas ISO YYYY-MM-DD). */
export function filterPatientsWithoutAppointmentInRange(
  patients: CrmPatientSummary[],
  fromIsoDate: string,
  toIsoDate: string,
): CrmPatientSummary[] {
  return patients.filter((p) => {
    if (!p.lastAppointmentAt) return true;
    const last = p.lastAppointmentAt;
    if (last >= fromIsoDate && last <= toIsoDate) return false;
    return true;
  });
}

/**
 * Exclui quem teve consulta realizada no período (IDs vindos do bundle FHIR).
 * Base para campanhas de resgate / “não atendidos no intervalo”.
 */
export function filterPatientsNotAttendedInPeriod(
  patients: CrmPatientSummary[],
  attendedPatientIds: Set<string>,
): CrmPatientSummary[] {
  if (attendedPatientIds.size === 0) return patients;
  return patients.filter((p) => !attendedPatientIds.has(p.patientId));
}

/** Mantém apenas pacientes presentes no conjunto (consulta no período). */
export function filterPatientsWithAppointmentInPeriod(
  patients: CrmPatientSummary[],
  matchingPatientIds: Set<string>,
): CrmPatientSummary[] {
  if (matchingPatientIds.size === 0) return [];
  return patients.filter((p) => matchingPatientIds.has(p.patientId));
}

function parseMonthDay(birthDate: string): { month: number; day: number } {
  const parts = birthDate.split("-");
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  return { month, day };
}

export function filterBirthdayToday(
  patients: CrmPatientSummary[],
  ref: Date,
): CrmPatientSummary[] {
  const m = ref.getMonth() + 1;
  const d = ref.getDate();
  return patients.filter((p) => {
    const md = parseMonthDay(p.birthDate);
    return md.month === m && md.day === d;
  });
}

export function filterBirthdayThisMonth(
  patients: CrmPatientSummary[],
  ref: Date,
): CrmPatientSummary[] {
  const m = ref.getMonth() + 1;
  return patients.filter((p) => {
    const md = parseMonthDay(p.birthDate);
    return md.month === m;
  });
}

/** Semana civil domingo–sábado (alinhada a getDay() em JS). */
export function filterBirthdayThisCalendarWeek(
  patients: CrmPatientSummary[],
  ref: Date,
): CrmPatientSummary[] {
  const start = new Date(ref);
  start.setDate(ref.getDate() - ref.getDay());
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  const y = ref.getFullYear();
  return patients.filter((p) => {
    const { month, day } = parseMonthDay(p.birthDate);
    const bday = new Date(y, month - 1, day);
    return bday >= start && bday <= end;
  });
}

export function filterPatientsWithPhone(
  patients: CrmPatientSummary[],
): CrmPatientSummary[] {
  return patients.filter((p) => Boolean(p.phone?.trim()));
}

/** Escopo de aniversário no diálogo (dia / semana civil / mês / resto do ano). */
export type BirthdayFilterScope = "day" | "week" | "month";

export function filterBirthdayRemainderOfYear(
  patients: CrmPatientSummary[],
  ref: Date,
): CrmPatientSummary[] {
  const y = ref.getFullYear();
  const end = new Date(y, 11, 31, 23, 59, 59, 999);
  const start = new Date(ref);
  start.setHours(0, 0, 0, 0);
  return patients.filter((p) => {
    const { month, day } = parseMonthDay(p.birthDate);
    const bday = new Date(y, month - 1, day);
    return bday >= start && bday <= end;
  });
}

export function filterBirthdayByScope(
  patients: CrmPatientSummary[],
  scope: BirthdayFilterScope,
  ref: Date,
): CrmPatientSummary[] {
  if (scope === "day") return filterBirthdayToday(patients, ref);
  if (scope === "week") return filterBirthdayThisCalendarWeek(patients, ref);
  if (scope === "month") return filterBirthdayThisMonth(patients, ref);
  return filterBirthdayRemainderOfYear(patients, ref);
}

export function messagesForTemplate(
  communications: FluxZapCommunicationRecord[],
  templateCode: string,
): FluxZapCommunicationRecord[] {
  return communications.filter((c) => c.templateCode === templateCode);
}

/** Envios FluxZap de um paciente (para diálogo de detalhes nas listas de atalho). */
export function messagesForPatient(
  communications: FluxZapCommunicationRecord[],
  patientId: string,
): FluxZapCommunicationRecord[] {
  return communications
    .filter(
      (c) => patientIdFromReference(c.subjectPatientReference) === patientId,
    )
    .sort((a, b) => (a.created < b.created ? 1 : -1));
}

export function isCommunicationsFocusMode(
  value: string,
): value is CommunicationsFocusMode {
  return value === "templates" || value === "patients";
}
